import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RELOAD_FLAG = "lazyWithRetry:reloaded";

const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message)
  );
};

const handleChunkFailure = (error: unknown): never => {
  if (typeof window === "undefined") throw error;
  const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG);
  if (!alreadyReloaded) {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    window.location.reload();
    // Return a never-resolving promise so React Suspense holds until reload.
    throw new Promise(() => {});
  }
  throw error;
};

export const clearLazyRetryFlag = () => {
  if (typeof window !== "undefined") sessionStorage.removeItem(RELOAD_FLAG);
};

export function importWithRetry<T>(factory: () => Promise<T>): Promise<T> {
  return factory().then(
    (mod) => {
      clearLazyRetryFlag();
      return mod;
    },
    (error) => {
      if (isChunkLoadError(error)) return handleChunkFailure(error);
      throw error;
    },
  );
}

// Use `ComponentType<any>` (not `<unknown>`) so components with typed props
// (e.g. memo()-wrapped components) flow through these generics. With `<unknown>`,
// the inferred T fails to satisfy the constraint because props can't be unknown.
type AnyComponent = ComponentType<any>;

export function lazyWithRetry<T extends AnyComponent>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() => importWithRetry(factory));
}

export type Preloadable<T extends AnyComponent> = LazyExoticComponent<T> & {
  preload: () => Promise<unknown>;
};

const moduleCache = new WeakMap<() => Promise<unknown>, Promise<unknown>>();

const sharedImport = <T>(factory: () => Promise<T>): Promise<T> => {
  const cached = moduleCache.get(factory) as Promise<T> | undefined;
  if (cached) return cached;
  const promise = importWithRetry(factory).catch((err) => {
    moduleCache.delete(factory);
    throw err;
  });
  moduleCache.set(factory, promise);
  return promise;
};

export function preloadable<T extends AnyComponent>(
  factory: () => Promise<{ default: T }>,
): Preloadable<T> {
  const Comp = lazy(() => sharedImport(factory)) as Preloadable<T>;
  Comp.preload = () => sharedImport(factory).catch(() => undefined);
  return Comp;
}
